import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, EMPTY } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ChartConfiguration } from 'chart.js';

import { DashboardWidgetsModule } from 'app/inter-chain/dashboard/widgets/dashboard-widgets.module';
import { FollowupPropertiesService } from 'app/inter-chain/followup/properties/followup-properties.service';
import { SurveyWrapperService } from 'app/shared/surveys/survey-wrapper/survey-wrapper.service';
import { TrendsService } from 'app/inter-chain/followup/trends/trends.service';
import { Average, DataResponse } from 'app/dashboard/models/WidgetDashboard.model.component';
import { TrendsData } from 'app/inter-chain/dashboard/models/TrendsData.model';
import { Survey } from 'app/survey-public/models';
import { DashboardWidgetCardComponent } from '../dashboard-widget-card/dashboard-widget-card.component';
import {
  buildChartDataset,
  emptyChartData,
  fillTimeSeriesGaps,
  DECREASE_COLOR,
  DECREASE_BG_COLOR,
  INCREASE_COLOR,
  INCREASE_BG_COLOR,
  NPS_CHART_OPTIONS,
} from '../../utils/chart-config.util';
import { getNPSQualificationKey } from '../../utils/qualifications.util';

@Component({
  selector: 'mh-nps-widget-container',
  templateUrl: './nps-widget-container.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DashboardWidgetsModule, DashboardWidgetCardComponent, TranslateModule],
  providers: [FollowupPropertiesService],
})
export class NpsWidgetContainerComponent {
  customerId = input.required<number>();
  productId = input.required<1 | 2>();
  enabled = input(true);

  npsData = signal<Average | null>(null);
  loading = signal(true);
  error = signal(false);
  ready = computed(() => !this.loading());
  chartData = signal<ChartConfiguration['data']>(emptyChartData());

  readonly npsChartOptions = NPS_CHART_OPTIONS;

  displayValue = computed(() => {
    const actual = this.npsData()?.nps?.actual;
    if (actual == null) return null;
    return actual <= 0 ? String(actual) : `+${actual}`;
  });

  displayChange = computed(() => this.npsData()?.nps?.variation ?? null);
  isIncrease = computed(() => (this.npsData()?.nps?.variation ?? 0) > 0);

  get product(): string {
    return this.productId() === 1 ? 'followup' : 'onsite';
  }
  get subtitle(): string {
    return this.productId() === 1 ? 'Follow Up' : 'Onsite';
  }
  get route(): string {
    return this.productId() === 1 ? '/followup/resumen' : '/onsite/resumen';
  }
  get demoRoute(): string {
    return this.productId() === 1 ? '/plg/followup' : '/plg/onsite';
  }
  get gaKey(): string {
    const suffix = this.enabled() ? 'active' : 'inactive';
    return this.productId() === 1 ? `corporatedash-viewfu-${suffix}` : `corporatedash-viewos-${suffix}`;
  }

  private destroyRef = inject(DestroyRef);
  private followupOnsiteNpsService = inject(FollowupPropertiesService);
  private surveyWrapperService = inject(SurveyWrapperService);
  private trendsService = inject(TrendsService);
  private translateService = inject(TranslateService);

  constructor() {
    combineLatest([toObservable(this.customerId), toObservable(this.productId), toObservable(this.enabled)])
      .pipe(
        switchMap(([, , enabled]) => {
          this.reset();
          if (!enabled) {
            this.setDisabled();
            return EMPTY;
          }
          return this.fetchData();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private reset(): void {
    this.npsData.set(null);
    this.loading.set(true);
    this.error.set(false);
    this.chartData.set(emptyChartData());
  }

  private setDisabled(): void {
    this.loading.set(false);
  }

  private fetchData() {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    const monthAgo = DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd');
    const dates = { startDate: monthAgo, endDate: today };

    return this.surveyWrapperService.getChainCustomerSurveys(`${this.customerId()}`, this.productId()).pipe(
      switchMap((surveys: Survey[]) => {
        if (!surveys?.length) {
          this.loading.set(false);
          return EMPTY;
        }
        const surveyIds = surveys.map((s) => s.id);
        const version = surveys[0]?.version || 2;

        return this.followupOnsiteNpsService
          .getNPS(dates, `${this.customerId()}`, this.productId(), surveyIds, version)
          .pipe(
            tap((response: DataResponse) => {
              if (!response) return;
              const avg = response.average;
              avg.description = this.translateService.instant(getNPSQualificationKey(avg?.nps?.actual));
              this.npsData.set(avg);
            }),
            switchMap((response: DataResponse) => {
              if (!response) return EMPTY;
              return this.trendsService
                .getHistoricNPS(dates, `${this.customerId()}`, this.productId(), 1, 'days', surveyIds, version)
                .pipe(
                  tap((trendsResponse: TrendsData) => {
                    const rawPeriods = trendsResponse.average.periods.map((p) => ({ period: p.period, value: p.data.nps ?? null }));
                    const filled = fillTimeSeriesGaps(rawPeriods);
                    const increase = (this.npsData()?.nps?.variation ?? 0) > 0;
                    this.chartData.set(
                      buildChartDataset(
                        filled.map((p) => p.period),
                        filled.map((p) => p.value as number),
                        increase ? INCREASE_COLOR : DECREASE_COLOR,
                        increase ? INCREASE_BG_COLOR : DECREASE_BG_COLOR,
                      ),
                    );
                  }),
                  catchError(() => EMPTY),
                );
            }),
            catchError(() => {
              this.error.set(true);
              return EMPTY;
            }),
          );
      }),
      catchError(() => {
        this.loading.set(false);
        return EMPTY;
      }),
      finalize(() => this.loading.set(false)),
    );
  }
}
